import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { Button } from "../../shadcn/ui/Button";
import { Badge } from "../../shadcn/ui/Badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../shadcn/ui/DropdownMenu";
import { Avatar, AvatarFallback, AvatarImage } from "../../shadcn/ui/Avatar";

export type HeaderChatUnread = {
  totalUnread: number;
  unreadThreads: Array<{
    conversationId: string;
    title: string;
    preview: string;
    unreadCount: number;
    avatarUrl?: string | null;
  }>;
  loading?: boolean;
  refresh?: () => void;
  onOpenThread: (conversationId: string) => void;
  onOpenAll: () => void;
};

type HeaderChatMessagesSlotProps = {
  chatUnread: HeaderChatUnread | undefined;
  showMessagesButton: boolean;
  isAuthenticated: boolean;
  variant: "compact" | "comfortable";
};

export function HeaderChatMessagesSlot({
  chatUnread,
  showMessagesButton,
  isAuthenticated,
  variant,
}: HeaderChatMessagesSlotProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isChatRoute =
    location.pathname === "/chat" || location.pathname.startsWith("/chat/");

  if (isChatRoute) return null;
  if (!showMessagesButton || !isAuthenticated) return null;

  const iconClass = variant === "comfortable" ? "h-5 w-5" : "h-4 w-4";
  const badgeClass =
    variant === "comfortable"
      ? "absolute -top-1.5 -right-1.5 min-w-[1.25rem] h-5 px-1 text-xs bg-red-600 text-white flex items-center justify-center rounded-full"
      : "absolute -top-2 -right-2 h-5 w-5 p-1 text-xs bg-red-600 text-white";

  if (!chatUnread) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          variant === "comfortable"
            ? "relative h-9 w-9 p-0 text-gray-600 hover:text-[#F97316] hover:bg-orange-50 outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            : "relative hover:bg-orange-100 text-black outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
        }
        aria-label="Messages"
        onClick={() => navigate("/chat")}
      >
        <MessageSquare className={iconClass} />
      </Button>
    );
  }

  const { totalUnread, unreadThreads, loading, refresh, onOpenThread, onOpenAll } = chatUnread;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) refresh?.();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={
            variant === "comfortable"
              ? "relative h-9 w-9 p-0 text-gray-600 hover:text-[#F97316] hover:bg-orange-50 outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
              : "relative hover:bg-orange-100 text-black outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
          }
          aria-label={totalUnread > 0 ? `Messages, ${totalUnread} unread` : "Messages"}
        >
          <MessageSquare className={iconClass} />
          {totalUnread > 0 ? (
            <Badge className={badgeClass}>{totalUnread > 99 ? "99+" : totalUnread}</Badge>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 sm:w-96 bg-white p-0 overflow-hidden">
        <DropdownMenuLabel className="px-3 py-2 text-black font-semibold border-b border-gray-100">
          Unread messages
        </DropdownMenuLabel>
        <div className="max-h-72 overflow-y-auto py-1">
          {loading ? (
            <div className="px-3 py-6 text-sm text-gray-500 text-center">Loading…</div>
          ) : unreadThreads.length === 0 ? (
            <div className="px-3 py-6 text-sm text-gray-500 text-center">You&apos;re all caught up.</div>
          ) : (
            unreadThreads.map((t) => (
              <DropdownMenuItem
                key={t.conversationId}
                className="flex items-start gap-3 px-3 py-2.5 cursor-pointer focus:bg-orange-50 rounded-none"
                onSelect={(e) => {
                  e.preventDefault();
                  onOpenThread(t.conversationId);
                }}
              >
                <Avatar className="h-9 w-9 shrink-0 border border-gray-200">
                  {t.avatarUrl ? (
                    <AvatarImage src={t.avatarUrl} alt="" />
                  ) : null}
                  <AvatarFallback className="text-xs bg-gray-100 text-gray-700">
                    {t.title.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{t.title}</span>
                    {t.unreadCount > 0 ? (
                      <Badge className="shrink-0 h-5 min-w-[1.25rem] px-1.5 text-[10px] bg-red-600 text-white">
                        {t.unreadCount > 99 ? "99+" : t.unreadCount}
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{t.preview}</p>
                </div>
              </DropdownMenuItem>
            ))
          )}
        </div>
        <DropdownMenuSeparator className="my-0" />
        <DropdownMenuItem
          className="text-[#F97316] focus:text-orange-700 focus:bg-orange-50 cursor-pointer rounded-none py-2.5"
          onSelect={(e) => {
            e.preventDefault();
            onOpenAll();
          }}
        >
          Open all messages
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
